import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { ADD_JOB } from '../../graphql/client/mutations';
import { AddJob, AddJobVariables } from '../../types/schemaTypes';
import { Routes, isValidDate } from '../../utils';

import moment from 'moment';

interface FieldErrors {
    jobNameError: string;
    crewError: string;
    regionError: string;
    districtError: string;
    customerError: string;
    cityError: string;
    stateError: string;
    latitudeError: string;
    longitudeError: string;
    zipError: string;
    phoneNumberError: string;
    startDateError: string;
    endDateError: string;
    epCompanyError: string;
    milesError: string;
    wellSiteGeoFenceTypeError: string;
    wellSiteRadiusError: string;
    stagingLatitudeError: string;
    stagingLongitudeError: string;
    stagingGeofenceTypeError: string;
    stagingRadiusError: string;
}

const initialErrorsState: FieldErrors = {
    jobNameError: '',
    crewError: '',
    regionError: '',
    districtError: '',
    customerError: '',
    cityError: '',
    stateError: '',
    latitudeError: '',
    longitudeError: '',
    zipError: '',
    phoneNumberError: '',
    startDateError: '',
    endDateError: '',
    epCompanyError: '',
    milesError: '',
    wellSiteGeoFenceTypeError: '',
    wellSiteRadiusError: '',
    stagingGeofenceTypeError: '',
    stagingLatitudeError: '',
    stagingLongitudeError: '',
    stagingRadiusError: '',
};

interface Suggestion {
    id: string | number;
    name: string;
}

const useAddJobProfileForm = (): any => {
    const [inputs, setInputs] = useState({
        addressLine1: '',
        addressLine2: '',
        contact: '',
        jobName: '',
        accessorial: 0,
        crew: '',
        crewId: 0,
        startDate: new Date(''),
        endDate: new Date(''),
        region: 0,
        district: 0,
        latitude: '',
        longitude: '',
        city: '',
        stateName: '',
        stateId: 0,
        zip: '',
        directions: '',
        customerName: '',
        customerId: 0,
        epCompany: '',
        epCompanyId: 0,
        phoneNumber: '',
        miles: '',
        stagingLatitude: '',
        stagingLongitude: '',
        wellsiteGeoFenceType: 'Circle',
        stagingGeoFenceType: '',
        wellsiteGeoFenceRadius: 300,
        stagingGeoFenceRadius: 0,
        wellsitePolygonCoordinates: [],
        stagingPolygonCoordinates: [],
        isBellyDump: false
    });
    const [fieldErrors, setFieldErrors] = useState(initialErrorsState);

    const [mutationError, setMutationError] = useState('');

    const openJobProfile = (jobId: string, jobName: string): void => {
        const URL = `${window.location.origin}${Routes.JOB_PROFILE.route}`;
        const newTab = window.open(URL + jobId, '_self');
        setTimeout(function (): void {
            if (newTab) {
                newTab.document.title = jobName;
            }
        }, 100);
    };

    const [addJob] = useMutation<AddJob, AddJobVariables>(ADD_JOB, {
        onCompleted: (res) => {
            if (res.addJob?.errors && res.addJob.errors.length === 0 && res.addJob.createdJobId != null) {
                openJobProfile(String(res.addJob.createdJobId), inputs.jobName);
                return;
            }
            setMutationError(res.addJob?.errors?.[0] ?? '');
        },
        onError: (e) => {
            if (CombinedGraphQLErrors.is(e)) {
                setMutationError(e.errors[0]?.message || e.message);
                console.log('GraphQL Error', e.errors);
            } else {
                setMutationError(e.message);
                console.log('Error', e.message);
            }
        },
    });

    const validateZipNumber = (value: any): void => {
        let zipError = '';
        if (value && !/^\d+$/.test(value)) {
            zipError = 'This field should only contain numeric values.';
        } else if (value && value.length > 5) {
            zipError = 'The number of characters must not exceed 5 digits.';
        }

        setFieldErrors({
            ...fieldErrors,
            zipError,
        });
    };

    const validatePhoneNumber = (value: any): void => {
        let phoneNumberError = '';
        if (value && !/^\d+$/.test(value)) {
            phoneNumberError = 'This field should only contain numeric values.';
        } else if (value && value.length > 10) {
            phoneNumberError = 'The number of characters must not exceed 10 digits.';
        }

        setFieldErrors({
            ...fieldErrors,
            phoneNumberError,
        });
    };

    const validateLatitude = (latitudeValue: any): void => {
        let latitudeError = '';
        if (latitudeValue && (latitudeValue < -90 || latitudeValue > 90)) {
            latitudeError = 'The valid range is from -90 to 90.';
        }

        setFieldErrors({
            ...fieldErrors,
            latitudeError,
        });
    };

    const validateStagingLatitude = (latitudeValue: any): void => {
        let stagingLatitudeError = '';
        if (latitudeValue && (latitudeValue < -90 || latitudeValue > 90)) {
            stagingLatitudeError = 'The valid range is from -90 to 90.';
        }

        if (latitudeValue == 0) {
            stagingLatitudeError = 'Value should not be 0. Please enter valid coordinates.';
        }

        setFieldErrors({
            ...fieldErrors,
            stagingLatitudeError,
        });
    };

    const validateRequiredFields = (): any => {
        let jobNameError = '';
        let crewError = '';
        let regionError = '';
        let districtError = '';
        let customerError = '';
        let cityError = '';
        let latitudeError = '';
        let longitudeError = '';
        let zipError = '';
        let stateError = '';
        let milesError = '';
        let wellSiteGeoFenceTypeError = '';
        let wellSiteRadiusError = '';
        let stagingGeofenceTypeError = '';
        let stagingRadiusError = '';
        let startDateError = '';
        let endDateError = '';
        const requiredFieldMessage = 'This field is required.';
        const cordinatesRequiredFieldMessage = 'Please draw polygon on the map.';
        if (inputs.jobName === '') {
            jobNameError = requiredFieldMessage;
        }

        if (inputs.crewId === 0) {
            crewError = requiredFieldMessage;
            crewError += ' You must select one of the suggested options.';
        }

        if (inputs.region === 0) {
            regionError = requiredFieldMessage;
        }

        if (inputs.district === 0) {
            districtError = requiredFieldMessage;
        }

        if (inputs.customerId === 0) {
            customerError = requiredFieldMessage;
            customerError += ' You must select one of the suggested options.';
        }

        if (!inputs.startDate || isNaN(new Date(inputs.startDate).getDate())) {
            startDateError = requiredFieldMessage;
        }

        if (!inputs.endDate || isNaN(new Date(inputs.endDate).getDate())) {
            endDateError = requiredFieldMessage;
        }

        if (inputs.city === '') {
            cityError = requiredFieldMessage;
        }

        if (inputs.latitude === '0' || inputs.latitude.toString() === '') {
            latitudeError = requiredFieldMessage;
            latitudeError += 'Value should not be 0. Please enter valid coordinates.';
        }

        if (inputs.longitude === '0' || inputs.longitude.toString() === '') {
            longitudeError = requiredFieldMessage;
            longitudeError += 'Value should not be 0. Please enter valid coordinates.';
        }

        if (inputs.zip === '') {
            zipError = requiredFieldMessage;
        }

        if (inputs.stateId === 0) {
            stateError = requiredFieldMessage;
            stateError += ' You must select one of the suggested options.';
        }

        if (inputs.accessorial !== 0 && inputs.miles === '') {
            milesError = 'Miles are required when an accessorial is selected.';
        }

        if (inputs.wellsiteGeoFenceType === '') {
            wellSiteGeoFenceTypeError = requiredFieldMessage;
        }

        if (inputs.wellsiteGeoFenceType == 'Circle' && (!inputs.wellsiteGeoFenceRadius || parseInt(inputs.wellsiteGeoFenceRadius.toString()) === 0)) {
            wellSiteRadiusError = requiredFieldMessage;
        }

        if (inputs.wellsiteGeoFenceType == 'Polygon' && inputs.wellsitePolygonCoordinates.length === 0) {
            wellSiteGeoFenceTypeError = cordinatesRequiredFieldMessage;
        }

        if (inputs.stagingGeoFenceType == 'Circle' && (!inputs.stagingGeoFenceRadius || parseInt(inputs.stagingGeoFenceRadius.toString()) === 0)) {
            stagingRadiusError = requiredFieldMessage;
        }

        if (inputs.stagingGeoFenceType == 'Polygon' && inputs.stagingPolygonCoordinates.length === 0) {
            stagingGeofenceTypeError = cordinatesRequiredFieldMessage;
        }


        return {
            jobNameError,
            crewError,
            regionError,
            districtError,
            customerError,
            startDateError,
            endDateError,
            cityError,
            latitudeError,
            longitudeError,
            zipError,
            stateError,
            milesError,
            wellSiteGeoFenceTypeError,
            wellSiteRadiusError,
            stagingGeofenceTypeError,
            stagingRadiusError,
        };
    };

    const validateLongitude = (longitudeValue: any): void => {
        let longitudeError = '';
        if (longitudeValue && (longitudeValue < -180 || longitudeValue > 180)) {
            longitudeError = 'The valid range is from -180 to 180.';
        }

        setFieldErrors({
            ...fieldErrors,
            longitudeError,
        });
    };

    const validateStagingLongitude = (longitudeValue: any): void => {
        let stagingLongitudeError = '';
        if (longitudeValue && (longitudeValue < -180 || longitudeValue > 180)) {
            stagingLongitudeError = 'The valid range is from -180 to 180.';
        }

        if (longitudeValue == 0) {
            stagingLongitudeError = 'Value should not be 0. Please enter valid coordinates.';
        }

        setFieldErrors({
            ...fieldErrors,
            stagingLongitudeError,
        });
    };

    const handleDropdownChange = (fieldName: string, newValue: string): void => {
        setInputs(inputs => ({ ...inputs, [fieldName]: +newValue }));
    };

    const removeErrors = (name: any): void => {
        switch (name) {
            case 'jobName': {
                setFieldErrors({
                    ...fieldErrors,
                    jobNameError: '',
                });
                break;
            }
            case 'city': {
                setFieldErrors({
                    ...fieldErrors,
                    cityError: '',
                });
                break;
            }
            case 'miles': {
                setFieldErrors({
                    ...fieldErrors,
                    milesError: '',
                });
                break;
            }
            case 'latitude': {
                setFieldErrors({
                    ...fieldErrors,
                    latitudeError: '',
                });
                break;
            }
            case 'longitude': {
                setFieldErrors({
                    ...fieldErrors,
                    longitudeError: '',
                });
                break;
            }
            case 'zip': {
                setFieldErrors({
                    ...fieldErrors,
                    zipError: '',
                });
                break;
            }
        }
    };

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        event.persist();
        removeErrors(event.target.name);
        setInputs(inputs => ({ ...inputs, [event.target.name]: event.target.value }));
    };

    const handleAutoCompleteChange = (inputName: string, value: Suggestion): void => {
        switch (inputName) {
            case 'crew': {
                setInputs({
                    ...inputs,
                    crew: value.name,
                    crewId: parseInt(value.id.toString()),
                });
                if (value.id !== 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        crewError: '',
                    });
                }
                break;
            }
            case 'customerName': {
                setInputs({
                    ...inputs,
                    customerName: value.name,
                    customerId: parseInt(value.id.toString()),
                    crew: '',
                    crewId: 0,
                });
                if (value.name !== '' && +value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        customerError: 'No customer found.',
                    });
                } else if (value.name === '' || (value.name !== '' && +value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        customerError: '',
                    });
                }
                break;
            }
            case 'epCompany': {
                setInputs({
                    ...inputs,
                    epCompany: value.name,
                    epCompanyId: parseInt(value.id.toString()),
                });

                if (value.name !== '' && value.id === 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        epCompanyError: 'You must select one of the suggested options.',
                    });
                } else if (value.name === '' || (value.name !== '' && value.id !== 0)) {
                    setFieldErrors({
                        ...fieldErrors,
                        epCompanyError: '',
                    });
                }
                break;
            }
            case 'stateName': {
                setInputs({
                    ...inputs,
                    stateName: value.name,
                    stateId: parseInt(value.id.toString()),
                });
                if (value.name !== '' && value.id !== 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        stateError: '',
                    });
                }
                break;
            }
            case 'region': {
                setInputs({
                    ...inputs,
                    region: +value.id,
                });
                if (value.id !== 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        regionError: '',
                    });
                }
                break;
            }
            case 'district': {
                setInputs({
                    ...inputs,
                    district: +value.id,
                });
                if (value.id !== 0) {
                    setFieldErrors({
                        ...fieldErrors,
                        districtError: '',
                    });
                }
                break;
            }
        }
    };

    const handleDateInputChange = (inputName: string, value: Date): void => {
        switch (inputName) {
            case 'startDate': {
                const startDate = !!value ? new Date(value) : null;
                let startDateError = '';

                if (!isValidDate(startDate)) {
                    startDateError = 'Please enter a valid date.';
                } else if (startDate && inputs.endDate && startDate > inputs.endDate) {
                    startDateError = 'Well start date should not be greater than end date.';
                }

                setFieldErrors({
                    ...fieldErrors,
                    startDateError,
                });
                break;
            }
            case 'endDate': {
                const endDate = !!value ? new Date(value) : null;
                let endDateError = '';

                if (!isValidDate(endDate)) {
                    endDateError = 'Please enter a valid date.';
                } else if (endDate && inputs.startDate && endDate < inputs.startDate) {
                    endDateError = 'Well end date should not be less than start date.';
                }

                setFieldErrors({
                    ...fieldErrors,
                    endDateError,
                });
                break;
            }
        }

        setInputs({
            ...inputs,
            [inputName]: value,
        });
    };

    const handleSubmit = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ): void => {
        if (event) {
            event.preventDefault();
        }

        const {
            jobNameError,
            crewError,
            regionError,
            districtError,
            customerError,
            cityError,
            latitudeError,
            longitudeError,
            zipError,
            stateError,
            milesError,
            startDateError,
            endDateError,
            wellSiteGeoFenceTypeError,
            wellSiteRadiusError,
            stagingGeofenceTypeError,
            stagingRadiusError,
        } = validateRequiredFields();

        setFieldErrors({
            ...fieldErrors,
            jobNameError,
            crewError,
            regionError,
            districtError,
            customerError,
            cityError,
            latitudeError,
            longitudeError,
            startDateError,
            endDateError,
            zipError,
            stateError,
            milesError,
            wellSiteGeoFenceTypeError,
            wellSiteRadiusError,
            stagingGeofenceTypeError,
            stagingRadiusError,
        });

        const { phoneNumberError, epCompanyError } = fieldErrors;

        if (
            jobNameError === '' &&
            crewError === '' &&
            regionError === '' &&
            districtError === '' &&
            customerError === '' &&
            cityError === '' &&
            stateError === '' &&
            latitudeError === '' &&
            longitudeError === '' &&
            zipError === '' &&
            phoneNumberError === '' &&
            startDateError === '' &&
            endDateError === '' &&
            epCompanyError === '' &&
            milesError === '' &&
            wellSiteGeoFenceTypeError === '' &&
            stagingGeofenceTypeError === ''
        ) {
            const addJobInput = {
                wellName: inputs.jobName,
                customerId: inputs.customerId,
                crewId: inputs.crewId,
                startJobDate: moment(inputs.startDate).utc().toISOString(),
                endJobDate: moment(inputs.endDate).utc().toISOString(),
                regionId: +inputs.region,
                districtId: +inputs.district,
                latitude: parseFloat(inputs.latitude.toString()),
                longitude: parseFloat(inputs.longitude.toString()),
                epCompanyId: inputs.epCompanyId === 0 ? null : inputs.epCompanyId,
                contact: inputs.contact,
                phoneNumber: inputs.phoneNumber,
                addressLine1: inputs.addressLine1,
                addressLine2: inputs.addressLine2,
                city: inputs.city,
                stateId: inputs.stateId,
                zip: inputs.zip,
                directions: inputs.directions,
                accessorialId: +inputs.accessorial,
                miles: inputs.miles,
                wellsiteGeoFenceType: inputs.wellsiteGeoFenceType,
                wellsiteGeoFenceRadius: parseInt(inputs.wellsiteGeoFenceRadius.toString()),
                wellsitePolygonCoordinates: inputs.wellsitePolygonCoordinates,
                stagingLatitude: parseFloat(inputs.stagingLatitude.toString()),
                stagingLongitude: parseFloat(inputs.stagingLongitude.toString()),
                stagingGeoFenceType: inputs.stagingGeoFenceType,
                stagingGeoFenceRadius: parseInt(inputs.stagingGeoFenceRadius.toString()),
                stagingPolygonCoordinates: inputs.stagingPolygonCoordinates,
                isBellyDump: inputs.isBellyDump,
            };
            addJob({ variables: { input: addJobInput } });
        }
    };

    const resetFormState = (): void => {
        setFieldErrors(initialErrorsState);
        setInputs({
            addressLine1: '',
            addressLine2: '',
            contact: '',
            jobName: '',
            accessorial: 0,
            crew: '',
            crewId: 0,
            startDate: new Date(''),
            endDate: new Date(''),
            region: 0,
            district: 0,
            latitude: '',
            longitude: '',
            city: '',
            stateName: '',
            stateId: 0,
            zip: '',
            directions: '',
            customerName: '',
            customerId: 0,
            epCompany: '',
            epCompanyId: 0,
            phoneNumber: '',
            miles: '',
            stagingLatitude: '',
            stagingLongitude: '',
            wellsiteGeoFenceType: 'Circle',
            stagingGeoFenceType: '',
            wellsiteGeoFenceRadius: 300,
            stagingGeoFenceRadius: 0,
            wellsitePolygonCoordinates: [],
            stagingPolygonCoordinates: [],
            isBellyDump: false,
        });
    };

    return {
        handleSubmit,
        handleDropdownChange,
        handleInputChange,
        handleAutoCompleteChange,
        handleDateInputChange,
        inputs,
        fieldErrors,
        validateRequiredFields,
        validateLatitude,
        validateLongitude,
        validateStagingLatitude,
        validateStagingLongitude,
        validateZipNumber,
        validatePhoneNumber,
        resetFormState,
        mutationError,
        setMutationError,
    };
};

export default useAddJobProfileForm;
